import { useCallback, useEffect, useMemo, useState } from 'react';
import { ProCard } from '@ant-design/pro-components';
import AdminProTable from '@/components/admin-pro-table';
import { App, Button } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSetState } from 'ahooks';
import Permission from '@/components/permission';

import {
    capitalOperationRecordsList,
    type CapitalOperationRecord,
    type CapitalOperationType,
} from '@/services/capital-operation-records';

import OperationLogDrawer from '@/components/OperationLogDrawer';
import ComponentDrawerForm, { type DrawerFormState } from './components/drawer-form';

import type { ProColumns } from '@ant-design/pro-components';
import type { PaginationProps } from 'antd';

const TYPE_MAP: Record<CapitalOperationType, string> = {
    32: '空投',
    33: '其他加线',
    34: '其他扣线',
};

const DEFAULT_PAGINATION: PaginationProps = {
    current: 1,
    pageSize: 20,
};

const PageFinanceCapitalOperationRecords: React.FC = () => {
    const { message } = App.useApp();
    const { formatMessage } = useIntl();
    const permissionScope = 'Financial-Management_Capital-Operation-Records';
    const addPermission = `${permissionScope}_Create`;

    const [pagination, setPagination] = useSetState<PaginationProps>({ ...DEFAULT_PAGINATION });
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState<CapitalOperationRecord[]>([]);
    const [drawerState, setDrawerState] = useSetState<DrawerFormState>({
        open: false,
        mode: 'create',
    });
    const [logDrawerOpen, setLogDrawerOpen] = useState(false);
    const [logBizId, setLogBizId] = useState<string | null>(null);

    const fetchList = useCallback(
        async (page: number = pagination.current as number, pageSize: number = pagination.pageSize as number) => {
            setLoading(true);
            try {
                const { data } = await capitalOperationRecordsList({ page, size: pageSize });
                if (data.code !== 200) {
                    message.error(data.msg || '获取列表失败');
                    return;
                }
                const meta = data.meta;
                setPagination({
                    current: meta?.page ?? page,
                    pageSize: meta?.size ?? pageSize,
                    total: meta?.total ?? data.data?.length ?? 0,
                });
                setDataSource(data.data ?? []);
            } catch (e: any) {
                message.error(e?.response?.data?.msg || e?.message || '获取列表失败');
            } finally {
                setLoading(false);
            }
        },
        [message, pagination.current, pagination.pageSize, setPagination],
    );

    useEffect(() => {
        fetchList();
    }, [fetchList]);

    const openCreate = useCallback(() => {
        setDrawerState({ open: true, mode: 'create' });
    }, [setDrawerState]);

    const openDetail = useCallback(
        (record: CapitalOperationRecord) => {
            setDrawerState({
                open: true,
                mode: 'detail',
                id: record.id,
                initialData: record,
            });
        },
        [setDrawerState],
    );

    const closeDrawer = useCallback(
        (reload?: boolean) => {
            setDrawerState({ open: false, mode: 'create' });
            if (reload) {
                fetchList(pagination.current as number, pagination.pageSize as number);
            }
        },
        [fetchList, pagination.current, pagination.pageSize, setDrawerState],
    );

    const columns = useMemo<ProColumns<CapitalOperationRecord>[]>(
        () => [
            {
                dataIndex: 'index',
                title: 'No.',
                valueType: 'index',
                width: 60,
            },
            {
                dataIndex: 'user_id',
                title: '用户ID',
                width: 180,
                ellipsis: true,
            },
            {
                dataIndex: 'type',
                title: '类型',
                width: 120,
                render: (_, record) => TYPE_MAP[record.type] ?? record.type,
            },
            {
                dataIndex: 'amount',
                title: '金额',
                width: 140,
                align: 'right',
                render: (_, record) => record.amount,
            },
            {
                dataIndex: 'reason',
                title: '原因',
                ellipsis: true,
                width: 220,
                render: (_, record) => record.reason || '-',
            },
            {
                title: '操作',
                valueType: 'option',
                fixed: 'right',
                width: 180,
                render: (_, record) => [
                    <Button type="link" key="detail" onClick={() => openDetail(record)}>
                        详情
                    </Button>,
                    <Button
                        type="link"
                        key="log"
                        onClick={() => {
                            setLogBizId(record.id);
                            setLogDrawerOpen(true);
                        }}
                    >
                        操作记录
                    </Button>,
                ],
            },
        ],
        [openDetail],
    );

    return (
        <Permission permission={permissionScope}>
            <ProCard title="资金操作列表">
                <AdminProTable<CapitalOperationRecord>
                    bordered
                    columns={columns}
                    dataSource={dataSource}
                    loading={loading}
                    rowKey="id"
                    search={false}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        onChange: (page, pageSize) => {
                            setPagination({ current: page, pageSize: pageSize ?? 20 });
                            fetchList(page, pageSize ?? 20);
                        },
                    }}
                    toolBarRender={() => [
                        <Permission permission={addPermission} key="create">
                            <Button type="primary" onClick={openCreate}>
                                创建
                            </Button>
                        </Permission>,
                    ]}
                    options={{
                        reload: () => fetchList(pagination.current as number, pagination.pageSize as number),
                        density: true,
                        setting: true,
                    }}
                    scroll={{ x: 800 }}
                />
            </ProCard>
            <ComponentDrawerForm state={drawerState} onClose={closeDrawer} />
            <OperationLogDrawer
                open={logDrawerOpen}
                bizId={logBizId}
                onClose={() => {
                    setLogDrawerOpen(false);
                    setLogBizId(null);
                }}
            />
        </Permission>
    );
};

export default PageFinanceCapitalOperationRecords;
